import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppNavigate, usePwaNav } from '../contexts/PwaNavContext';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { API_URL } from '../utils/api';
import { ArrowLeft, Plus, Minus, Gift, Edit2, X as XIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import CustomizationModal from '../components/CustomizationModal';
import type { ProductOptionGroup } from '../types';
import '../pages/RestaurantMenuPage.css'; // rm-page / rm-cart-bar / rm-content
import '../pages/OrderStatus.css';        // os-upsell-card grid — share single card style across upsell + gift
import './GiftPage.css';

// ── Types ────────────────────────────────────────────────────

interface OrderItem {
  id: string;
  quantity: number;
  product: { id: string; name: string; category?: string };
}

interface OrderData {
  id: string;
  status: string;
  qrCode?: string;
  restaurant?: { id: string; name: string };
  items?: OrderItem[];
  customer?: { id: string; name: string | null };
}

interface Product {
  id: string;
  name: string;
  description?: string | null;
  price: number;
  category?: string;
  imageUrl?: string | null;
  stockToday?: number | null;
  instantGiveout?: boolean;
  isEditable?: boolean;
  optionGroups?: ProductOptionGroup[] | null;
}

interface CartItem {
  tempId: string;
  productId: string;
  quantity: number;
  customization?: Record<string, string | string[]> | null;
}

// ── Helpers ─────────────────────────────────────────────────

const foodEmoji = (category?: string): string => {
  const map: Record<string, string> = {
    drink: '🥤', drinks: '🥤', beverage: '🥤',
    salad: '🥗',
    soup: '🍲',
    dessert: '🍰',
    meat: '🥩',
    pizza: '🍕',
    burger: '🍔',
    side: '🍟', sides: '🍟',
    main: '🍽️',
  };
  return map[category?.toLowerCase().trim() || ''] || '🍽️';
};

function friendInitials(name: string | null | undefined): string {
  if (!name) return '?';
  return name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);
}

function formatCustomization(cust?: Record<string, string | string[]> | null): string {
  if (!cust) return '';
  return Object.values(cust)
    .map((v) => (Array.isArray(v) ? v.join(', ') : v))
    .filter(Boolean)
    .join(' · ');
}

function computePrice(basePrice: number, groups: ProductOptionGroup[], cust: Record<string, string | string[]>): number {
  let total = basePrice;
  for (const group of groups) {
    const selected = cust[group.label];
    if (!selected) continue;
    const vals = Array.isArray(selected) ? selected : [selected];
    for (const v of vals) {
      const choice = group.choices.find((c) => c.label === v);
      if (choice?.priceModifier != null) total += Number(choice.priceModifier);
    }
  }
  return total;
}

function getUnitPrice(product: Product, cust?: Record<string, string | string[]> | null): number {
  if (cust && product.optionGroups?.length) {
    return computePrice(product.price, product.optionGroups, cust);
  }
  return product.price;
}

// ── Component ────────────────────────────────────────────────

export default function GiftPage() {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const { navData } = usePwaNav();
  const [searchParams] = useSearchParams();
  const { token } = useCustomerAuth();

  // navData populated when navigated within PWA shell; searchParams when opened via direct URL
  const orderId = navData.orderId ?? searchParams.get('orderId') ?? '';

  const [order, setOrder] = useState<OrderData | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [recProducts, setRecProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'recs' | 'menu'>('recs');

  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [customizeProduct, setCustomizeProduct] = useState<{ product: Product; cartItem?: CartItem } | null>(null);

  const [sending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const sendingRef = useRef(false);

  // ── Cart helpers ──────────────────────────────────────────

  const getCartItemsForProduct = (productId: string) =>
    cartItems.filter((c) => c.productId === productId);

  const getTotalQtyForProduct = (productId: string) =>
    getCartItemsForProduct(productId).reduce((sum, c) => sum + c.quantity, 0);

  const addToCart = (productId: string, product: Product, openCustomize = false) => {
    const editable = Array.isArray(product.optionGroups) && product.optionGroups.length > 0;
    if (openCustomize && editable) {
      setCustomizeProduct({ product });
      return;
    }
    const tempId = `gift-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setCartItems((prev) => [...prev, { tempId, productId, quantity: 1, customization: undefined }]);
  };

  const changeQty = (tempId: string, delta: number, maxStock: number | null) => {
    setCartItems((prev) =>
      prev
        .map((c) => {
          if (c.tempId !== tempId) return c;
          const totalForProduct = prev
            .filter((ci) => ci.productId === c.productId)
            .reduce((s, ci) => s + ci.quantity, 0);
          const newQty = c.quantity + delta;
          if (newQty <= 0) return { ...c, quantity: 0 };
          if (delta > 0 && maxStock !== null && totalForProduct >= maxStock) return c;
          return { ...c, quantity: newQty };
        })
        .filter((c) => c.quantity > 0)
    );
  };

  const removeFromCart = (tempId: string) => {
    setCartItems((prev) => prev.filter((c) => c.tempId !== tempId));
  };

  const updateCartItemCustomization = (tempId: string, cust: Record<string, string | string[]>) => {
    setCartItems((prev) => prev.map((c) => (c.tempId === tempId ? { ...c, customization: cust } : c)));
  };

  const cartTotal = cartItems.reduce((sum, ci) => {
    const product = products.find((p) => p.id === ci.productId);
    if (!product) return sum;
    return sum + getUnitPrice(product, ci.customization) * ci.quantity;
  }, 0);

  const cartCount = cartItems.reduce((sum, ci) => sum + ci.quantity, 0);

  // ── Fetch ──────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    if (!orderId) {
      setError(t('gift.errorNoOrder', 'No order specified.'));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const base = API_URL || '';
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.get(`${base}/api/gifts/order-for-gift/${orderId}`, { headers });
      const fetchedOrder: OrderData = data.order;
      let fetchedProducts: Product[] = data.products ?? [];

      const isReady = fetchedOrder.status === 'READY';
      const stockOk = (p: Product) => (p.stockToday ?? 1) > 0 && (!isReady || p.instantGiveout);
      fetchedProducts = fetchedProducts.filter(stockOk);

      // Flatten ML recommendations (r1 + r2 — own restaurant only; v1/v2 venue cross-sells skipped for gift)
      const recs = (data.recommendations ?? {}) as { r1?: Array<{ product: Product }>; r2?: Array<{ product: Product }> };
      const seen = new Set<string>();
      const recList: Product[] = [];
      for (const entry of [...(recs.r1 ?? []), ...(recs.r2 ?? [])]) {
        const p = entry?.product;
        if (!p || seen.has(p.id)) continue;
        if (!stockOk(p)) continue;
        seen.add(p.id);
        recList.push(p);
      }

      setOrder(fetchedOrder);
      setProducts(fetchedProducts);
      setRecProducts(recList);
      setActiveTab(recList.length > 0 ? 'recs' : 'menu');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        t('gift.errorLoad', 'Failed to load gift options.');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [orderId, token, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Send gifts (payment integration pending — see ApiPay.kz) ──

  const handleSend = async () => {
    if (cartItems.length === 0 || !orderId || sendingRef.current) return;
    const paymentDisabled = import.meta.env.VITE_PAYMENT_DISABLED === 'true';
    if (!paymentDisabled) {
      setError(t('payment.unavailable', 'Оплата временно недоступна'));
      return;
    }
    sendingRef.current = true;
    setError(null);
    try {
      const base = API_URL || '';
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const productById = new Map<string, Product>(products.concat(recProducts).map((p) => [p.id, p]));
      // Send each cart item as a gift — backend creates OrderItem with giftedByCustomerId
      for (const ci of cartItems) {
        await axios.post(
          `${base}/api/gifts/send`,
          { orderId, productId: ci.productId, suppressNotification: true },
          { headers },
        );
      }
      // One consolidated push at the end — pass items: [{ name, quantity }]
      const itemsForPush = cartItems.map((ci) => ({
        name: productById.get(ci.productId)?.name ?? 'Подарок',
        quantity: ci.quantity,
      }));
      await axios.post(
        `${base}/api/gifts/notify-sent`,
        { orderId, items: itemsForPush },
        { headers },
      ).catch(() => { /* notification is best-effort */ });
      setCartItems([]);
      setSendSuccess(true);
      setTimeout(() => {
        setSendSuccess(false);
        navigate('/');
      }, 1500);
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || (err as Error).message
        || t('gift.errorSend', 'Не удалось отправить подарок');
      setError(msg);
    } finally {
      sendingRef.current = false;
    }
  };

  // ── Loading / Error / Empty ──────────────────────────────

  if (loading) {
    return (
      <main className="rm-page" aria-busy="true">
        <div className="rm-skeleton rm-skeleton--cover" />
        <div className="rm-content">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rm-skeleton rm-skeleton--row" />
          ))}
        </div>
      </main>
    );
  }

  if (error && !order) {
    return (
      <main className="rm-page rm-page--centered" role="alert">
        <div className="gift-empty">
          <p>{error}</p>
          <div className="gift-empty-actions">
            <Button variant="primary" size="lg" onClick={fetchData}>
              {t('common.retry', 'Повторить')}
            </Button>
            <button type="button" className="gift-empty-link" onClick={() => navigate('/')}>
              {t('gift.goHome', 'На главную')}
            </button>
          </div>
        </div>
      </main>
    );
  }

  // ── Render ─────────────────────────────────────────────────

  const friendName = order?.customer?.name ?? t('gift.friend', 'Friend');
  const restaurantName = order?.restaurant?.name ?? '';
  const orderItems = order?.items ?? [];
  const isReadyOrder = order?.status === 'READY';

  const renderItem = (product: Product) => {
    const items = getCartItemsForProduct(product.id);
    const totalQty = getTotalQtyForProduct(product.id);
    const st = product.stockToday;
    const maxStock = st != null && !isNaN(Number(st)) && Number(st) >= 0 ? Number(st) : null;
    const atMax = maxStock !== null && totalQty >= maxStock;
    const editable = Array.isArray(product.optionGroups) && product.optionGroups.length > 0;

    const handlePlus = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (atMax) return;
      if (editable) addToCart(product.id, product, true);
      else if (items[0]) changeQty(items[0].tempId, 1, maxStock);
      else addToCart(product.id, product, false);
    };
    const handleMinus = (e: React.MouseEvent) => {
      e.stopPropagation();
      const last = items[items.length - 1];
      if (last) changeQty(last.tempId, -1, maxStock);
    };

    const handleCardClick = () => {
      if (totalQty === 0 && maxStock !== 0) {
        if (editable) addToCart(product.id, product, true);
        else addToCart(product.id, product, false);
      }
    };

    const mediaStyle: React.CSSProperties = product.imageUrl
      ? ({ '--os-card-bg-image': `url(${product.imageUrl})` } as React.CSSProperties)
      : {};

    return (
      <div
        key={product.id}
        className="os-upsell-card"
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
      >
        <div
          className={`os-upsell-media ${product.imageUrl ? 'os-upsell-media--img' : ''}`}
          style={mediaStyle}
        >
          {product.category && (
            <span className="os-upsell-cat-pill">{product.category}</span>
          )}
        </div>
        <div className="os-upsell-body">
          <div className="os-upsell-name">{product.name}</div>
          <div className="os-upsell-footer">
            <div>
              <span className="os-upsell-price">{product.price.toLocaleString('ru')} ₸</span>
              {maxStock !== null && totalQty < maxStock && (
                <span className="os-upsell-stock-hint">осталось {maxStock - totalQty}</span>
              )}
            </div>
            {totalQty === 0 ? (
              <button
                type="button"
                className="os-upsell-add"
                onClick={(e) => { e.stopPropagation(); handlePlus(e); }}
                disabled={maxStock === 0}
                aria-label={t('orderStatus.addToOrder', 'Добавить')}
              >
                <Plus size={16} strokeWidth={2.4} />
              </button>
            ) : (
              <div className="os-upsell-qty">
                <button
                  type="button"
                  className="os-upsell-qty-btn"
                  onClick={(e) => { e.stopPropagation(); handleMinus(e); }}
                  aria-label="−"
                >
                  <Minus size={12} strokeWidth={2.6} />
                </button>
                <span className="os-upsell-count">{totalQty}</span>
                <button
                  type="button"
                  className="os-upsell-qty-btn is-add"
                  onClick={(e) => { e.stopPropagation(); handlePlus(e); }}
                  disabled={atMax}
                  aria-label="+"
                >
                  <Plus size={12} strokeWidth={2.6} />
                </button>
              </div>
            )}
          </div>
          {editable && items.length > 0 && (
            <div className="gift-variants">
              {items.map((item) => (
                <div key={item.tempId} className="gift-variant">
                  <span className="gift-variant-name">
                    {item.quantity}× {formatCustomization(item.customization) || t('orderStatus.default', 'Стандарт')}
                  </span>
                  <button
                    type="button"
                    className="gift-variant-icon"
                    onClick={(e) => { e.stopPropagation(); setCustomizeProduct({ product, cartItem: item }); }}
                    aria-label={t('orderStatus.edit', 'Изменить')}
                  >
                    <Edit2 size={12} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="gift-variant-icon"
                    onClick={(e) => { e.stopPropagation(); removeFromCart(item.tempId); }}
                    aria-label={t('orderStatus.remove', 'Удалить')}
                  >
                    <XIcon size={12} aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <main className="rm-page">
      {/* Standalone back row — sits outside the dark hero so it has breathing space */}
      <div className="gift-topbar">
        <button
          type="button"
          className="gift-topbar-back"
          onClick={() => navigate('/')}
          aria-label={t('common.back', 'Назад')}
        >
          <ArrowLeft size={18} aria-hidden="true" />
        </button>
      </div>

      {/* Hero card with title / friend info / their order */}
      <div className="gift-hero">
        <div className="gift-hero-eyebrow">
          {t('gift.pageTitle', 'Угостить друга')}
        </div>
        <div className="gift-hero-row">
          <div className="gift-hero-avatar" aria-hidden="true">
            {friendInitials(friendName)}
          </div>
          <div className="gift-hero-info">
            <div className="gift-hero-name">{friendName}</div>
            {restaurantName && <div className="gift-hero-restaurant">{restaurantName}</div>}
          </div>
        </div>
        {orderItems.length > 0 && (
          <div className="gift-hero-order">
            <div className="gift-hero-order-label">{t('gift.theyOrdered', 'Их заказ:')}</div>
            <div className="gift-hero-chips">
              {orderItems.map((item) => (
                <span key={item.id} className="gift-chip">
                  {foodEmoji(item.product.category ?? '')} {item.product.name}
                  {item.quantity > 1 ? ` ×${item.quantity}` : ''}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {isReadyOrder && (
        <div className="gift-warning">
          {t('gift.readyWarning', 'Заказ готов — доступны только блюда мгновенной выдачи.')}
        </div>
      )}
      {error && order && (
        <div className="gift-toast" role="status">
          <span>{error}</span>
          <button
            type="button"
            className="gift-toast-close"
            onClick={() => setError(null)}
            aria-label="Закрыть"
          >
            <XIcon size={14} aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Tab nav — Recs / Menu — same style as OrderStatus os-tabs */}
      {(recProducts.length > 0 || products.length > 0) && (
        <div className="os-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'recs'}
            className={`os-tab ${activeTab === 'recs' ? 'os-tab--active' : ''}`}
            onClick={() => setActiveTab('recs')}
            disabled={recProducts.length === 0}
          >
            {t('gift.tabRecs', 'Рекомендации')}
            {recProducts.length > 0 && (
              <span className="os-tab-badge">{recProducts.length}</span>
            )}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'menu'}
            className={`os-tab ${activeTab === 'menu' ? 'os-tab--active' : ''}`}
            onClick={() => setActiveTab('menu')}
          >
            {t('gift.tabMenu', 'Меню')}
            <span className="os-tab-badge">{products.length}</span>
          </button>
        </div>
      )}

      <div className="rm-content">
        {activeTab === 'recs' && recProducts.length > 0 && (
          <section className="rm-cat">
            <h2 className="rm-cat-title">
              {t('gift.recsTitle', 'Подойдёт другу')}
              <span className="gift-cat-sub">
                {' '}— {t('gift.recsHint', 'по его вкусам и текущему заказу')}
              </span>
            </h2>
            <div className="os-upsell-grid">{recProducts.map(renderItem)}</div>
          </section>
        )}
        {activeTab === 'menu' && products.length > 0 && (
          <section className="rm-cat">
            <h2 className="rm-cat-title">{t('gift.chooseItem', 'Выберите угощение')}</h2>
            <div className="os-upsell-grid">{products.map(renderItem)}</div>
          </section>
        )}
        {((activeTab === 'recs' && recProducts.length === 0) ||
          (activeTab === 'menu' && products.length === 0)) && (
          <div className="gift-empty">
            <p>{t('gift.noProducts', 'Нет доступных угощений.')}</p>
          </div>
        )}
      </div>

      {/* Sticky cart bar */}
      {cartCount > 0 && (
        <div className="rm-cart-bar rm-cart-bar--active">
          <div className="rm-cart-bar-badge">
            <Gift size={16} aria-hidden="true" />
            <span>{cartCount}</span>
          </div>
          <Button
            variant="primary"
            size="lg"
            className="rm-cart-bar-btn"
            onClick={handleSend}
            disabled={sending}
          >
            {sending
              ? t('gift.sending', 'Отправляем…')
              : `${t('gift.confirm', 'Угостить')} · ${cartTotal.toLocaleString()} ₸`}
          </Button>
        </div>
      )}

      {/* Success overlay */}
      {sendSuccess && createPortal(
        <div className="gift-success-overlay">
          <div className="gift-success-card">
            <div className="gift-success-emoji">🎁</div>
            <div className="gift-success-title">{t('gift.sentTitle', 'Подарок отправлен!')}</div>
            <div className="gift-success-sub">{t('gift.sentSub', 'Друг увидит на странице заказа')}</div>
          </div>
        </div>,
        document.body
      )}

      {/* Customization modal */}
      {customizeProduct && customizeProduct.product.optionGroups && createPortal(
        <CustomizationModal
          productName={customizeProduct.product.name}
          basePrice={customizeProduct.product.price}
          optionGroups={customizeProduct.product.optionGroups}
          initialCustomization={customizeProduct.cartItem?.customization || null}
          onSave={(cust) => {
            if (customizeProduct.cartItem) {
              updateCartItemCustomization(customizeProduct.cartItem.tempId, cust);
            } else {
              const tempId = `gift-${Date.now()}-${Math.random().toString(36).slice(2)}`;
              setCartItems((prev) => [...prev, {
                tempId,
                productId: customizeProduct.product.id,
                quantity: 1,
                customization: cust,
              }]);
            }
            setCustomizeProduct(null);
          }}
          onClose={() => setCustomizeProduct(null)}
        />,
        document.body
      )}
    </main>
  );
}
